import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

function capturesBasePath({ assetId, taskId }) {
  if (taskId) {
    return `${API_URL}/v1/admin/tasks/${taskId}/captures`;
  }
  return `${API_URL}/v1/admin/assets/${assetId}/captures`;
}

export default function useCaptureMedia({ assetId, taskId, enabled = true }) {
  const isMounted = useIsMounted();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scopeKey = taskId || assetId;

  const fetchCaptures = useCallback(async () => {
    if (!enabled || !scopeKey) return;
    if (!assetId && !taskId) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(capturesBasePath({ assetId, taskId }));
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setError(err.message || 'Failed to load field captures');
      setItems([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [assetId, taskId, enabled, scopeKey, isMounted]);

  useEffect(() => {
    fetchCaptures();
  }, [fetchCaptures]);

  const fetchDownloadUrl = useCallback(
    async (captureId, variant = 'original') => {
      if (!scopeKey || !captureId) return null;
      const res = await fetchWithAuth(
        `${capturesBasePath({ assetId, taskId })}/${captureId}/download?variant=${encodeURIComponent(variant)}`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = body.errors?.join(', ') || body.error || res.statusText;
        throw new Error(message);
      }
      return body.url;
    },
    [assetId, taskId, scopeKey]
  );

  const openCapture = useCallback(
    async (captureId) => {
      try {
        const url = await fetchDownloadUrl(captureId, 'original');
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        if (!isMounted.current) return;
        console.error(err);
        window.alert(err.message || 'Could not open photo');
      }
    },
    [fetchDownloadUrl, isMounted]
  );

  return {
    items,
    loading,
    error,
    refresh: fetchCaptures,
    fetchDownloadUrl,
    openCapture,
  };
}
